import type { MasqueradeCompaniesQuery } from "../../gql/graphql";
import type { ApiService } from "../api/api-service";
import type { Query } from "../api/query";
import type { AuthService, MasqueradeEvent } from "./auth-service";
import { bound, observable } from "ecce-react";
import { compareByName } from "../../util/compare";
import { masqueradeUsersQuery } from "./masquerade.gql";


export type MasqueradeCompany = Readonly<{ id: string, name: string }>;
export type MasqueradePerson = Readonly<{ id: string, name: string }>;

export class MasqueradeController {
	#api: ApiService;
	#auth: AuthService;

	#query: Query<MasqueradeCompaniesQuery> | null = null;

	#dialogOpen = false;
	get dialogOpen(): boolean { return this.#dialogOpen; }
	@observable() private set dialogOpen(value: boolean) { this.#dialogOpen = value; }

	#loading = false;
	get loading(): boolean { return this.#loading; }
	@observable() private set loading(value: boolean) { this.#loading = value; }

	#companyOptions: readonly MasqueradeCompany[] = [];
	get companyOptions(): readonly MasqueradeCompany[] { return this.#companyOptions; }
	@observable() private set companyOptions(value: readonly MasqueradeCompany[]) { this.#companyOptions = value; }

	#selectedCompany: MasqueradeCompany | null = null;
	get selectedCompany(): MasqueradeCompany | null { return this.#selectedCompany; }
	@observable() private set selectedCompany(value: MasqueradeCompany | null) { this.#selectedCompany = value; }

	#peopleOptions: readonly MasqueradePerson[] = [];
	get peopleOptions(): readonly MasqueradePerson[] { return this.#peopleOptions; }
	@observable() private set peopleOptions(value: readonly MasqueradePerson[]) { this.#peopleOptions = value; }

	#selectedPerson: MasqueradePerson | null = null;
	get selectedPerson(): MasqueradePerson | null { return this.#selectedPerson; }
	@observable() private set selectedPerson(value: MasqueradePerson | null) { this.#selectedPerson = value; }

	#allPeople: readonly MasqueradePerson[] = [];
	#peopleByCompanyId: ReadonlyMap<string, readonly MasqueradePerson[]> | null = null;

	constructor(api: ApiService, auth: AuthService) {
		this.#api = api;
		this.#auth = auth;
	}

	@bound()
	openDialog(): void {
		this.dialogOpen = true;
		if(!this.#query && !this.#auth.masquerading) {
			this.loading = true;
			this.#query = this.#api.query(masqueradeUsersQuery)
				.on("loading", () => this.loading = true)
				.on("data", ({ data }) => {
					this.loading = false;
					const companies: Record<string, MasqueradeCompany> = {};
					const allPeople: MasqueradePerson[] = [];
					const peopleByCompanyId: Record<string, MasqueradePerson[]> = {};

					for(const userProfile of data.userProfilesPaginated.userProfiles) {
						const person: MasqueradePerson = {
							id: userProfile.id,
							name: userProfile.firstName + " " + userProfile.lastName,
						};

						if(userProfile.company) {
							companies[userProfile.company.id] ??= {
								id: userProfile.company.id,
								name: userProfile.company.name,
							};
							(peopleByCompanyId[userProfile.company.id] ??= []).push(person);
						}

						allPeople.push({
							id: userProfile.id,
							name: userProfile.firstName + " " + userProfile.lastName,
						});
					}

					this.companyOptions = [ ...Object.values(companies) ];
					this.#allPeople = allPeople.sort(compareByName);
					this.#peopleByCompanyId = new Map(Object.entries(peopleByCompanyId));

					this.selectCompany(null);
				});
		}
		this.#auth.on("masquerade", this.#handleMasquerade);
	}

	@bound()
	closeDialog(): void {
		this.dialogOpen = false;
		this.#auth.off("masquerade", this.#handleMasquerade);
	}

	#handleMasquerade = (ev: MasqueradeEvent) => {
		if(ev.status !== "failed") {
			this.closeDialog();
		}
	};

	selectCompany(company: MasqueradeCompany | null): void {
		if(!company) {
			this.selectedCompany = null;
			this.peopleOptions = this.#allPeople;
			return;
		}

		if(!this.#peopleByCompanyId?.has(company.id)) {
			this.selectCompany(null);
			return;
		}

		this.selectedCompany = company;
		this.peopleOptions = this.#peopleByCompanyId.get(company.id)!;
		if(this.selectedPerson && this.peopleOptions.includes(this.selectedPerson)) {
			this.selectPerson(null);
		}
	}

	selectPerson(person: MasqueradePerson | null): void {
		this.selectedPerson = person;
	}

	@bound()
	submit(): void {
		if(!this.selectedPerson) {
			return;
		}
		this.loading = true;
		this.#auth.beginMasquerade(this.selectedPerson.id);
	}
}
